import { useEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import { CheckCircleOutlined } from "@ant-design/icons";
import { useSearchParams } from "react-router-dom";
import { useSelector } from "react-redux";
import createDOMPurify from "dompurify";
import { debounce } from "lodash";
import { App } from "antd";

import {
  OptionType,
  SuggestionStatus,
  SuggestionType,
} from "@/shared/utils/constants.js";
import {
  useCreateAnswerMutation,
  useDeleteAnswerMutation,
  useGetQuestionByIdQuery,
  useLazyGetAnswersQuery,
  useLazyGetQuestionSuggestionStatusQuery,
  usePatchAnswerMutation,
  useUpdateAnswerMutation,
} from "@/redux/service.js";
import BaseDropdown from "@/components_v2/FormFields/Dropdown/BaseDropdown.tsx";
import CustomChoiceDropdown from "@/components_v2/FormFields/Dropdown/CustomChoiceDropdown.tsx";
import BaseAccordion from "@/components_v2/Accordion/BaseAccordion.tsx";
import BaseSpinner from "@/components_v2/Spiner/BaseSpinner.tsx";
import SliderDropdown from "@/components_v2/FormFields/Dropdown/SliderDropdown.tsx";
import DebouncedInput from "@/components/form/DebouncedInput.jsx";
import { selectCurrentUser } from "@/redux/authentication/selectors.ts";
import Evidence from "@/features/survey/form/components/Evidence.tsx";
import BaseTooltip from "@/components_v2/Tooltip/BaseTooltip.tsx";
import BaseSvgRenderer from "@/components_v2/Svg/BaseSvgRenderer.tsx";
import QuestionSuggestions from "./QuestionSuggestions.tsx";
import SuggestionStatusBadge from "./SuggestionStatusBadge.tsx";
import Info from "@/assets/icons/info-circle.svg?react";
import Check from "@/assets/icons/check-circle.svg?react";
import { useOrganizationId } from "@/shared/hooks/useOrganizationId.ts";

import "../styles.css";

const Question = ({ questionId }) => {
  const { t } = useTranslation();
  const DOMPurify = createDOMPurify(window);
  const { notification } = App.useApp();
  const [searchParams] = useSearchParams();

  const sampleOrganizationId = searchParams.get("sampleOrganizationId");
  const currentOrganizationId = useOrganizationId();
  const { data: question } = useGetQuestionByIdQuery({
    questionId,
  });
  const [
    createAnswer,
    { isLoading: isCreateAnswerLoading, isSuccess: isCreateAnswerSuccess },
  ] = useCreateAnswerMutation();
  const [
    updateAnswer,
    { isLoading: isUpdateAnswerLoading, isSuccess: isUpdateAnswerSuccess },
  ] = useUpdateAnswerMutation();
  const [
    patchAnswer,
    { isLoading: isPatchAnswerLoading, isSuccess: isPatchAnswerSuccess },
  ] = usePatchAnswerMutation();
  const [
    deleteAnswer,
    { isLoading: isDeleteAnswerLoading, isSuccess: isDeleteAnswerSuccess },
  ] = useDeleteAnswerMutation();
  const [getQuestionSuggestionsStatus, { data: suggestionStatusData }] =
    useLazyGetQuestionSuggestionStatusQuery();
  const currentUser = useSelector(selectCurrentUser);
  const [getAnswers, { data: answersData }] = useLazyGetAnswersQuery();
  const isAnswerSubmitting =
    isCreateAnswerLoading ||
    isUpdateAnswerLoading ||
    isPatchAnswerLoading ||
    isDeleteAnswerLoading;
  const isAnswerSuccess =
    isCreateAnswerSuccess ||
    isUpdateAnswerSuccess ||
    isPatchAnswerSuccess ||
    isDeleteAnswerSuccess;

  const otherOption = question?.options.find(
    (opt) => opt.type === OptionType.CHOICE_TEXT.value,
  );
  const hasSlider = question?.options.find(
    (opt) => opt.relatedOptions.length > 0,
  );
  const questionType =
    question?.options.length > 0 ? question.options[0].type : null;
  const [answerMap, setAnswerMap] = useState({});
  const [customOptions, setCustomOptions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const hasInitializedRef = useRef(false);

  const handleAnswerApiError = (response) => {
    const errorData = response?.error?.data;
    if (!errorData) return;

    let code = errorData.code || errorData.errors?.[0]?.code;
    if (!code) {
      code = "default";
    }

    notification.error({
      message: t(`survey.errors.${code}`, errorData.params),
      showProgress: true,
      duration: 10,
    });
  };

  const handleMutationResponse = (response) => {
    if (response?.error) {
      handleAnswerApiError(response);
      return null;
    }
    return response?.data ?? null;
  };

  const handleDeleteResponse = (response) => {
    if (response?.error) {
      handleAnswerApiError(response);
      return false;
    }
    return true;
  };

  useEffect(() => {
    const isSuggestionStatusApplied =
      suggestionStatusData?.suggestionStatus === SuggestionStatus.APPLIED;
    if (isSuggestionStatusApplied) {
      setShowSuggestions(false);
    }
  }, [suggestionStatusData]);

  useEffect(() => {
    if (!hasInitializedRef.current) {
      return;
    }

    const timeout = setTimeout(() => {
      if (questionId && question?.suggestionSource) {
        getQuestionSuggestionsStatus(questionId);
      }
    }, 1000);

    return () => clearTimeout(timeout);
  }, [answerMap]);

  useEffect(() => {
    if (question?.suggestionSource) {
      getQuestionSuggestionsStatus(questionId);
    }
  }, [question]);

  const disabledSuggestions = useMemo(() => {
    if (!answerMap || !question || !questionType) {
      return { itemIds: [], choiceTextValues: [] };
    }

    let itemIds = [];
    let choiceTextValues = [];

    if (
      [
        OptionType.SINGLE_CHOICE.value,
        OptionType.MULTIPLE_CHOICE.value,
      ].includes(questionType)
    ) {
      itemIds = Object.keys(answerMap).map(Number);

      const choiceTextOption = question.options.find(
        (opt) => opt.type === OptionType.CHOICE_TEXT.value,
      );
      if (choiceTextOption && answerMap[choiceTextOption.id]) {
        choiceTextValues = answerMap[choiceTextOption.id].textAnswer || [];
      }
    } else if (questionType === OptionType.PROMPT.value) {
      itemIds = question.options.reduce((acc, option) => {
        if (answerMap[option.id]) {
          const selectedBlockIds = answerMap[option.id].attachedBlocks.map(
            (block) => block.id,
          );
          return acc.concat(selectedBlockIds);
        }
        return acc;
      }, []);
    }

    return { itemIds, choiceTextValues };
  }, [questionType, answerMap, question]);

  useEffect(() => {
    if (answersData) {
      answersData.forEach((answer) => {
        const textAnswer = answer.textAnswer || [];
        if (textAnswer) {
          setCustomOptions((prev) => [...new Set([...prev, ...textAnswer])]);
        }
        setAnswerMap((prev) => ({
          ...prev,
          [answer.option]: {
            ...answer,
            textAnswer,
          },
        }));
      });
      hasInitializedRef.current = true;
    }
  }, [answersData]);

  useEffect(() => {
    if (question) {
      getAnswers({
        question: question.id,
        pagination: "off",
        organization: sampleOrganizationId || currentOrganizationId,
      });
    }
  }, [question]);

  const areAllAnswersComplete = useMemo(() => {
    if (answersData && answersData.length > 0) {
      return answersData.every((answer) => answer.isComplete);
    }

    return false;
  }, [answersData]);

  const handleSingleChoiceSelect = async (selectedOptionId) => {
    if (!question || !currentUser) return;

    if (selectedOptionId == null) {
      const existingAnswerOptionId = Object.keys(answerMap).map(Number)[0];
      const existingAnswerId = existingAnswerOptionId
        ? answerMap[existingAnswerOptionId]?.id
        : null;

      if (existingAnswerId) {
        const response = await deleteAnswer(existingAnswerId);
        if (handleDeleteResponse(response)) {
          setAnswerMap({});
        }
      } else {
        setAnswerMap({});
      }
      return;
    }

    const selectedOption = question.options.find(
      (opt) => opt.id === selectedOptionId,
    );
    if (!selectedOption) return;

    const existingAnswerOptionId = Object.keys(answerMap).map(Number)[0];
    const existingAnswerId = existingAnswerOptionId
      ? answerMap[existingAnswerOptionId]?.id
      : null;

    const existingAnswer = answerMap?.selectedOptionId;
    setAnswerMap({ [selectedOptionId]: null });

    if (existingAnswerId) {
      const response = await updateAnswer({
        id: existingAnswerId,
        data: {
          option: selectedOption.id,
          question: selectedOption.question,
          organization: currentUser.organization?.id,
        },
      });

      const data = handleMutationResponse(response);
      if (data) {
        setAnswerMap({ [selectedOption.id]: data });
      } else {
        if (existingAnswerOptionId) {
          setAnswerMap({ [existingAnswerOptionId]: existingAnswer });
        } else {
          setAnswerMap({});
        }
      }
    } else {
      const response = await createAnswer({
        option: selectedOption.id,
        question: selectedOption.question,
        organization: currentUser.organization?.id,
      });

      const data = handleMutationResponse(response);
      if (data) {
        setAnswerMap({ [selectedOption.id]: data });
      } else {
        if (existingAnswerOptionId) {
          setAnswerMap({ [existingAnswerOptionId]: existingAnswer });
        } else {
          setAnswerMap({});
        }
      }
    }
  };

  const handleMultipleChoiceSelect = async (selectedValues) => {
    if (!question || !currentUser) return;

    const currentSelectedOptions = Object.keys(answerMap).map(Number);

    const optionsToAdd = selectedValues.filter(
      (v) => !currentSelectedOptions.includes(v),
    );
    const optionsToRemove = currentSelectedOptions.filter(
      (v) =>
        !selectedValues.includes(v) && (!otherOption || v !== otherOption.id),
    );

    for (const optionId of optionsToAdd) {
      const option = question.options.find((opt) => opt.id === optionId);
      if (option) {
        setAnswerMap((prev) => ({
          ...prev,
          [option.id]: null,
        }));

        const response = await createAnswer({
          option: option.id,
          question: option.question,
          organization: currentUser.organization?.id,
        });

        const data = handleMutationResponse(response);
        if (data) {
          setAnswerMap((prev) => ({
            ...prev,
            [option.id]: data,
          }));
        } else {
          setAnswerMap((prev) => {
            const updated = { ...prev };
            delete updated[option.id];
            return updated;
          });
        }
      }
    }

    for (const optionId of optionsToRemove) {
      const option = question.options.find((opt) => opt.id === optionId);
      const answer = answerMap[option.id];

      if (option && answer) {
        const response = await deleteAnswer(answer.id);
        if (handleDeleteResponse(response)) {
          setAnswerMap((prev) => {
            const updated = { ...prev };
            delete updated[option.id];
            return updated;
          });
        } else {
          setAnswerMap((prev) => ({
            ...prev,
            [option.id]: answer,
          }));
        }
      }
    }
  };

  const handleTextAnswer = async (textValues, option) => {
    const existingAnswerId = answerMap[option.id]?.id;
    if (
      !question ||
      !currentUser ||
      (textValues.length === 0 && !existingAnswerId)
    )
      return;

    setCustomOptions((prev) => [...new Set([...prev, ...textValues])]);

    const previousTextAnswer = answerMap[option.id]?.textAnswer || [];

    setAnswerMap((prev) => ({
      ...prev,
      [option.id]: {
        ...prev[option.id],
        textAnswer: textValues,
      },
    }));

    if (existingAnswerId) {
      if (textValues.length === 0) {
        const response = await deleteAnswer(existingAnswerId);
        if (handleDeleteResponse(response)) {
          setAnswerMap((prev) => {
            const updated = { ...prev };
            delete updated[option.id];
            return updated;
          });
        } else {
          setAnswerMap((prev) => ({
            ...prev,
            [option.id]: {
              ...prev[option.id],
              textAnswer: previousTextAnswer,
            },
          }));
        }
      } else {
        const response = await patchAnswer({
          id: answerMap[option.id].id,
          data: { textAnswer: textValues },
        });
        const data = handleMutationResponse(response);
        if (!data) {
          setAnswerMap((prev) => ({
            ...prev,
            [option.id]: {
              ...prev[option.id],
              textAnswer: previousTextAnswer,
            },
          }));
        }
      }
    } else {
      const response = await createAnswer({
        option: option.id,
        question: option.question,
        organization: currentUser.organization.id,
        textAnswer: textValues,
      });

      const data = handleMutationResponse(response);
      if (data) {
        setAnswerMap((prev) => ({
          ...prev,
          [option.id]: {
            ...data,
            textAnswer: textValues,
          },
        }));
      } else {
        setAnswerMap((prev) => ({
          ...prev,
          [option.id]: {
            ...prev[option.id],
            textAnswer: previousTextAnswer,
          },
        }));
      }
    }
  };

  const handleRegularOptionsInSliderDropdown = async (
    currentAnswerMap,
    numericValues,
  ) => {
    const newNumericValues = numericValues.reduce((acc, choice) => {
      acc[choice.choice] = {
        ...(currentAnswerMap[choice.choice] || {}),
        textAnswer: choice.scale != null ? [choice.scale] : [],
      };
      return acc;
    }, {});
    setAnswerMap((prev) => {
      const result = { ...newNumericValues };

      if (otherOption && prev[otherOption.id]) {
        result[otherOption.id] = prev[otherOption.id];
      }

      return result;
    });

    for (const optionId of Object.keys(currentAnswerMap).filter(
      (key) =>
        key !== otherOption?.id?.toString() &&
        !Object.keys(newNumericValues).includes(key),
    )) {
      const answer = currentAnswerMap[optionId];
      if (answer) {
        const response = await deleteAnswer(answer.id);
        handleDeleteResponse(response);
      }
    }

    for (const choice of numericValues) {
      const currentScaleValue =
        currentAnswerMap[choice.choice]?.textAnswer?.[0];
      const scaleHasChanged = choice.scale !== currentScaleValue;
      const hasNoRelatedOptions = !question.options.find(
        (opt) => opt.id === choice.choice,
      )?.relatedOptions?.length;

      if (
        choice.scale == null ||
        scaleHasChanged ||
        hasNoRelatedOptions.length === 0
      ) {
        let response;
        if (currentAnswerMap[choice.choice]?.id) {
          response = await patchAnswer({
            id: currentAnswerMap[choice.choice].id,
            data: { textAnswer: [choice.scale] },
          });
        } else {
          response = await createAnswer({
            option: choice.choice,
            question: question.id,
            organization: currentUser.organization.id,
            ...(choice.scale ? { textAnswer: [choice.scale] } : {}),
          });
        }

        const data = handleMutationResponse(response);
        if (data) {
          setAnswerMap((prev) => ({
            ...prev,
            [choice.choice]: {
              ...data,
              textAnswer: [choice.scale],
            },
          }));
        }
      }
    }
  };

  const handleCustomOptionsInSliderDropdown = async (otherValues) => {
    const existingChoices = [...(answerMap[otherOption?.id]?.textAnswer || [])];

    const newCustomChoices = otherValues.map((v) => {
      const existingIndex = existingChoices.findIndex(
        (c) => c.choice === v.choice,
      );
      if (existingIndex >= 0) {
        return {
          ...existingChoices[existingIndex],
          choice: v.choice,
          scale: v.scale || null,
        };
      } else {
        return {
          choice: v.choice,
          scale: v.scale || null,
        };
      }
    });
    setAnswerMap((prev) => ({
      ...prev,
      [otherOption.id]: {
        ...prev[otherOption.id],
        textAnswer: newCustomChoices,
      },
    }));

    const hasNewScaledCustomChoices = newCustomChoices.every(
      (c) => c.scale != null,
    );

    if (hasNewScaledCustomChoices) {
      if (answerMap[otherOption.id]?.id) {
        const textAnswer = newCustomChoices.filter((c) => c.scale != null);
        if (textAnswer.length === 0) {
          const response = await deleteAnswer(answerMap[otherOption.id].id);
          handleDeleteResponse(response);
          return;
        }
        const response = await patchAnswer({
          id: answerMap[otherOption.id].id,
          data: {
            textAnswer,
          },
        });
        handleMutationResponse(response);
      } else {
        const response = await createAnswer({
          option: otherOption.id,
          question: question.id,
          organization: currentUser.organization.id,
          textAnswer: newCustomChoices.filter((c) => c.scale != null),
        });

        const data = handleMutationResponse(response);
        if (data) {
          setAnswerMap((prev) => ({
            ...prev,
            [otherOption.id]: {
              ...data,
              textAnswer: existingChoices,
            },
          }));
        }
      }
    }
  };

  const handleSliderDropdownChanges = async (values) => {
    const currentAnswerMap = { ...answerMap };
    const regularValues = values.filter((v) => typeof v.choice === "number");
    const otherValues = values.filter((v) => typeof v.choice === "string");
    const hasChangedRegularValues = regularValues.some((choice) => {
      const currentScaleValue =
        currentAnswerMap[choice.choice]?.textAnswer?.[0];
      return choice.scale !== currentScaleValue;
    });

    const currentNumericKeys = Object.keys(currentAnswerMap)
      .filter((key) => key !== otherOption?.id?.toString())
      .map(Number);

    const selectedNumericKeys = regularValues.map((v) => v.choice).map(Number);
    const hasAddedOrRemovedRegularOptions =
      selectedNumericKeys.some((key) => !currentNumericKeys.includes(key)) ||
      currentNumericKeys.some((key) => !selectedNumericKeys.includes(key));
    if (hasChangedRegularValues || hasAddedOrRemovedRegularOptions) {
      await handleRegularOptionsInSliderDropdown(
        currentAnswerMap,
        regularValues,
      );
      return;
    }

    await handleCustomOptionsInSliderDropdown(otherValues);
  };

  const handleCustomChoiceChange = async (selectedValues) => {
    const values = Array.isArray(selectedValues)
      ? selectedValues
      : [selectedValues];
    if (hasSlider) {
      await handleSliderDropdownChanges(values);
    } else {
      const textValues = values.filter((i) => typeof i === "string");
      const selectValues = values.filter((i) => typeof i === "number");

      if (otherOption) {
        const hasExistingTextAnswers =
          answerMap[otherOption.id]?.textAnswer?.length > 0;
        if (textValues.length > 0 || hasExistingTextAnswers) {
          await handleTextAnswer(textValues, otherOption);
        }
      }

      if (questionType === OptionType.SINGLE_CHOICE.value) {
        if (selectValues.length > 0) {
          await handleSingleChoiceSelect(selectValues[0]);
        }
      } else {
        await handleMultipleChoiceSelect(selectValues);
      }
    }
  };

  const handleAttachBlocks = async (option, answer, blocks) => {
    if (answer) {
      if (blocks.length === 0) {
        const response = await deleteAnswer(answer.id);
        if (handleDeleteResponse(response)) {
          setAnswerMap((prev) => {
            const updated = { ...prev };
            delete updated[option.id];
            return updated;
          });
        }
      } else {
        const response = await updateAnswer({
          id: answer.id,
          data: {
            ...answer,
            attachedBlockIds: blocks.map((i) => i.id),
          },
        });
        const data = handleMutationResponse(response);
        if (data) {
          setAnswerMap({ [option.id]: data });
        }
      }
    } else {
      const response = await createAnswer({
        option: option.id,
        question: option.question,
        organization: currentUser.organization.id,
        attachedBlockIds: blocks.map((i) => i.id),
      });
      const data = handleMutationResponse(response);
      if (data) {
        setAnswerMap({ [option.id]: data });
      }
    }
  };

  const handleApplySuggestions = async (value) => {
    if (!question) return;

    if (value.type === SuggestionType.TEXT) {
      const textOption = question.options.find((opt) =>
        [OptionType.TEXT.value, OptionType.NUMBER.value].includes(opt.type),
      );
      if (textOption) {
        await handleTextAnswer([value.value], textOption);
      }
    } else if (value.type === SuggestionType.CHOICE) {
      if (value.optionIds.size === 0) return;

      const choiceTextOption = question.options.find(
        (opt) => opt.type === OptionType.CHOICE_TEXT.value,
      );
      const regularOptionIds = Array.from(value.optionIds).filter(
        (id) => id !== choiceTextOption?.id,
      );

      if (questionType === OptionType.SINGLE_CHOICE.value) {
        if (value.choiceTextValues.size > 0 && choiceTextOption) {
          // For single choice, only one CHOICE_TEXT can be selected
          const choiceTextValue = Array.from(value.choiceTextValues)[0];
          await handleTextAnswer([choiceTextValue], choiceTextOption);
        } else if (regularOptionIds.length > 0) {
          await handleSingleChoiceSelect(regularOptionIds[0]);
        }
      } else if (questionType === OptionType.MULTIPLE_CHOICE.value) {
        // Handle regular options
        if (regularOptionIds.length > 0) {
          const currentSelectedOptions = Object.keys(answerMap).map(Number);
          const allSelectedOptions = [
            ...new Set([
              ...currentSelectedOptions.filter(
                (id) => id !== choiceTextOption?.id,
              ),
              ...regularOptionIds,
            ]),
          ];
          await handleMultipleChoiceSelect(allSelectedOptions);
        }

        // Handle CHOICE_TEXT options - merge with existing
        if (value.choiceTextValues.size > 0 && choiceTextOption) {
          const existingTextAnswers =
            answerMap[choiceTextOption.id]?.textAnswer || [];
          const allTextAnswers = [
            ...new Set([
              ...existingTextAnswers,
              ...Array.from(value.choiceTextValues),
            ]),
          ];
          await handleTextAnswer(allTextAnswers, choiceTextOption);
        }
      }
    } else if (value.type === SuggestionType.PROMPT) {
      if (value.blockIds.size === 0) return;

      const targetOption = question.options.find(
        (opt) => opt.type === OptionType.PROMPT.value,
      );

      if (targetOption) {
        const existingAnswer = answerMap[targetOption.id];
        const currentBlockIds = (existingAnswer?.attachedBlocks || []).map(
          (b) => b.id,
        );
        const allBlockIds = [
          ...new Set([...currentBlockIds, ...Array.from(value.blockIds)]),
        ];
        await handleAttachBlocks(
          targetOption,
          existingAnswer,
          allBlockIds.map((blockId) => ({ id: blockId })),
        );
      }
    }

    setShowSuggestions(false);
  };

  const renderOptionList = () => {
    return question.options
      .slice()
      .sort((a, b) => a.id - b.id)
      .map((option) => {
        if (
          [OptionType.TEXT.value, OptionType.NUMBER.value].includes(option.type)
        ) {
          const textRows = option.textFieldRows
            ? option.textFieldRows
            : option.type === OptionType.NUMBER.value
              ? 3
              : 1;
          return (
            <DebouncedInput
              key={option.id}
              placeholder={question.placeholder}
              initialValue={answerMap[option.id]?.textAnswer || ""}
              onChange={(value) => handleTextAnswer(value, option)}
              disabled={!!sampleOrganizationId}
              rows={textRows}
              className="text-option"
              type={option.type === OptionType.NUMBER.value ? "number" : "text"}
              isTextArea={
                option.type === OptionType.TEXT.value &&
                option.textFieldRows != null
              }
            />
          );
        } else if (option.type === OptionType.PROMPT.value) {
          return (
            <Evidence
              key={option.id}
              option={option}
              answer={answerMap[option.id]}
              handleSubmit={(blocks) =>
                handleAttachBlocks(option, answerMap[option.id], blocks)
              }
            />
          );
        }
        return null;
      });
  };

  const questionContent = useMemo(() => {
    if (!question) return null;
    const isMultiple = questionType === OptionType.MULTIPLE_CHOICE.value;
    const selectedValues = Object.keys(answerMap).map(Number);

    if (
      [
        OptionType.SINGLE_CHOICE.value,
        OptionType.MULTIPLE_CHOICE.value,
      ].includes(questionType) ||
      hasSlider
    ) {
      const options = question.options
        .filter((option) => option.type !== OptionType.CHOICE_TEXT.value)
        .map((option) => ({
          value: option.id,
          label: option.label,
          relatedOptions: option.relatedOptions.map((relatedOption) => ({
            value: relatedOption.id,
            label: relatedOption.label,
          })),
        }));

      if (hasSlider) {
        const dropdownOptions = [...options];
        let sliderValues = [];

        // Handle standard options
        Object.keys(answerMap)
          .filter((id) => id !== otherOption?.id?.toString())
          .forEach((optionId) => {
            const numericId = Number(optionId);
            const option = question.options.find((opt) => opt.id === numericId);

            if (option) {
              sliderValues.push({
                choice: numericId,
                scale: answerMap[optionId]?.textAnswer?.[0],
                label: option.label,
              });
            }
          });

        // Handle custom choices from otherOption
        if (otherOption && answerMap[otherOption.id]) {
          const customChoices = Array.isArray(
            answerMap[otherOption.id].textAnswer,
          )
            ? answerMap[otherOption.id].textAnswer
            : [];

          customChoices.forEach((item) => {
            if (item && typeof item === "object" && item.choice) {
              dropdownOptions.push({
                value: item.choice,
                label: item.choice,
                relatedOptions: (otherOption?.relatedOptions || []).map(
                  (relatedOption) => ({
                    value: relatedOption.id,
                    label: relatedOption.label,
                  }),
                ),
              });
              sliderValues.push({
                choice: item.choice,
                scale: item.scale,
                label: item.choice,
              });
            }
          });
        }

        return (
          <SliderDropdown
            placeholder={question.placeholder}
            multiple={isMultiple}
            secondaryPromptText={question.secondaryPromptText}
            value={sliderValues}
            options={dropdownOptions}
            helpText={question.relatedOptionsHelpText}
            onChange={handleCustomChoiceChange}
            customInputOptions={{ show: true }}
            disabled={!!sampleOrganizationId}
            allowClear={true}
          />
        );
      }

      if (otherOption) {
        const customValues = isMultiple
          ? [
              ...selectedValues.filter((i) => i !== otherOption.id),
              ...(answerMap[otherOption.id]?.textAnswer || []),
            ]
          : selectedValues[0];
        return (
          <CustomChoiceDropdown
            placeholder={question.placeholder}
            options={[
              ...options,
              ...customOptions.map((text) => ({
                value: text,
                label: text,
              })),
            ]}
            onChange={handleCustomChoiceChange}
            multiple={isMultiple}
            value={customValues}
            disabled={!!sampleOrganizationId}
            allowClear={true}
          />
        );
      } else {
        return (
          <BaseDropdown
            options={options}
            placeholder={question.placeholder}
            onChange={
              isMultiple ? handleMultipleChoiceSelect : handleSingleChoiceSelect
            }
            multiple={isMultiple}
            value={isMultiple ? selectedValues : selectedValues[0]}
            disabled={!!sampleOrganizationId}
            allowClear={true}
          />
        );
      }
    }

    return renderOptionList();
  }, [
    question,
    otherOption,
    questionType,
    hasSlider,
    answerMap,
    customOptions,
  ]);

  return (
    question && (
      <BaseAccordion
        className="question-container"
        expandIconPosition="end"
        defaultActiveKey={[questionId]}
        collapsible="icon"
        items={[
          {
            key: question.id,
            label: (
              <div className="flex flex-col gap-150">
                <div className="flex gap-200 items-center justify-between">
                  <div className="flex gap-100 items-center">
                    {areAllAnswersComplete && (
                      <BaseSvgRenderer
                        fill="var(--green-500)"
                        component={Check}
                        stroke="var(--base-white)"
                      />
                    )}

                    <h4 className="header-m-medium">
                      {question.index + 1 + "."}
                    </h4>

                    <h4 className="header-m-medium">
                      {question.label}
                      {question.isRequired && " *"}
                    </h4>

                    {question.tooltip && (
                      <BaseTooltip
                        title={
                          <span
                            dangerouslySetInnerHTML={{
                              __html: DOMPurify.sanitize(question.tooltip, {
                                USE_PROFILES: { html: true },
                                ADD_ATTR: ["target"],
                              }),
                            }}
                          />
                        }
                      >
                        <BaseSvgRenderer
                          component={Info}
                          fill="var(--neutral-500-10)"
                          stroke="var(--neutral-500)"
                        />
                      </BaseTooltip>
                    )}
                  </div>

                  {suggestionStatusData?.suggestionStatus &&
                  !sampleOrganizationId ? (
                    <SuggestionStatusBadge
                      suggestionStatus={suggestionStatusData.suggestionStatus}
                      sourceSurvey={suggestionStatusData.sourceSurvey}
                      isDisabled={showSuggestions}
                      onClick={() => {
                        if (
                          suggestionStatusData.suggestionStatus !==
                          SuggestionStatus.APPLIED
                        ) {
                          setShowSuggestions((prev) => !prev);
                        }
                      }}
                    />
                  ) : null}
                </div>

                <p
                  className="question-description body-m-regular"
                  onClick={(e) => {
                    if (e.target.tagName === "A") {
                      e.stopPropagation();
                    }
                  }}
                  dangerouslySetInnerHTML={{
                    __html: DOMPurify.sanitize(question.description, {
                      USE_PROFILES: { html: true },
                      ADD_ATTR: ["target"],
                    }),
                  }}
                />

                {question.helpText && (
                  <p className="body-s-regular question-help-text">
                    {question.helpText}
                  </p>
                )}
              </div>
            ),
            isActive: true,
            children: (
              <div className="flex flex-col gap-300">
                {showSuggestions && (
                  <QuestionSuggestions
                    questionId={question.id}
                    questionType={questionType}
                    onDismiss={() => setShowSuggestions(false)}
                    onApply={handleApplySuggestions}
                    disabledSuggestions={disabledSuggestions}
                  />
                )}

                {questionContent}
                {isAnswerSubmitting && (
                  <div className="flex items-center gap-200">
                    <BaseSpinner size="extra-small" isFullWidth={false} />
                    <p>{t("labels.syncingInProgress")}</p>
                  </div>
                )}
                {isAnswerSuccess && !isAnswerSubmitting && (
                  <div className="flex items-center gap-200">
                    <CheckCircleOutlined style={{ color: "green" }} />
                    <p>{t("labels.syncingDone")}</p>
                  </div>
                )}
              </div>
            ),
          },
        ]}
      />
    )
  );
};

Question.propTypes = {
  questionId: PropTypes.number.isRequired,
};

export default Question;
